import React, {lazy, Suspense, useCallback, useContext, useEffect} from 'react'

import './App.css'
import {AuthContext, PageContext} from "@lb/mlib-components";
import {Navigate, Outlet, Route, Routes, useLocation} from "react-router-dom";
import CenteredBox from "./components/ui/CenteredBox";
import {LoadingButton} from "@mui/lab";
import Reconcile from "./components/production/Reconcile";
import HolidaysPage from "./pages/HolidaysPage";
import Samples from "./components/production/Samples/Samples";
import {trackScreen} from "./utils/reactGA";
import Logistics from "./components/production/Logistics";


const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProductionPage = lazy(() => import('./pages/ProductionPage'));
const QualityPage = lazy(() => import('./pages/QualityPage'));
const ForPickupPage = lazy(() => import('./pages/ForPickupPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const SalesOrder = lazy(() => import('./components/audit/SalesOrder'));
const Shipment = lazy(() => import('./components/audit/Shipment/Shipment'));
const TransitPage = lazy(() => import('./pages/TransitPage'));
const CouriersPage = lazy(() => import('./pages/CouriersPage'));
const TargetsPage = lazy(() => import('./pages/TargetsPage'));
const FreeItemsPage = lazy(() => import('./pages/FreeItemsPage'));
const EditFreeItem = lazy(() => import('./components/freeitems/EditFreeItem'));
const ProductionOrder = lazy(() => import('./components/production/ProductionOrder'));
const ProductionOrderByShape = lazy(() => import('./components/production/ProductionOrderByShape'));
const ProductionOrderByFinish = lazy(() => import('./components/production/ProductionOrderByFinish'));
const StockItem = lazy(() => import('./components/audit/StockItem'));
const AuditStockPage = lazy(() => import('./pages/AuditStockPage'));
const AuditOrdersPage = lazy(() => import('./pages/AuditOrdersPage'));
const QCIssuedPage = lazy(() => import('./pages/QCIssuedPage'));
const Issue = lazy(() => import('./components/production/QC/Issue'));
const AuditClonedPage = lazy(() => import('./pages/AuditClonedPage'));
const AuditWarehousePage = lazy(() => import('./pages/AuditWarehousePage'));
const AuditShipmentPage = lazy(() => import('./pages/AuditShipmentPage'));
const SchedulerPage = lazy(() => import('./pages/SchedulerPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));

const DimsPage = lazy(() => import("./pages/DimsPage"));
const TrackersPage = lazy(() => import("./pages/TrackersPage"));
const Stations = lazy(() => import("./components/stations/Stations"));
const Station = lazy(() => import("./components/stations/Station"));
const Printers = lazy(() => import("./components/printers/Printers"));
const Printer = lazy(() => import("./components/printers/Printer"));
const Home = lazy(() => import("./pages/home/home"));
const Forbidden = lazy(() => import("./pages/403"));
const NotFound = lazy(() => import("./pages/404"));
const FulfillmentPage = lazy(() => import("./pages/FulfilmentPage"));

const Finance = lazy(() => import( "./components/production/Finance"));
const AuditLBShipPage = lazy(() => import( "./pages/AuditLBShipPage"));
const GlobalSearchPage = lazy(() => import( "./pages/GlobalSearchPage"));

export default function RouterRoutes({load}) {
    const authCtx = useContext(AuthContext)
    const pageCtx = useContext(PageContext)
    const location = useLocation()

    // track location(url-pathname) change to kick out user without api call
    const isLoggedIn = useCallback(() => location && authCtx.isLoggedIn(), [authCtx, location])

    const ProtectedRoute = useCallback(({isAllowed, redirectPath, children}) => {
        if (isAllowed) return <Outlet/>
        else {
            if (redirectPath) return <Navigate to={redirectPath} replace/>
            else return <Forbidden/>
        }
    }, [])

    useEffect(() => {
        trackScreen(pageCtx.title + ' - ' + location.pathname);
    }, [pageCtx.title]);

    useEffect(() => {
        if (window.location.pathname !== '/login')
            pageCtx.updateCurrentUrl(window.location.pathname)
    }, [pageCtx])

    const {build} = window['runConfig'];

    return (
        <Suspense fallback={<CenteredBox><LoadingButton loading> </LoadingButton></CenteredBox>}>
            <Routes>

                <Route path='/'>
                    <Route path='/' element={<Home/>}/>
                </Route>

                {/* `Shared` paths */}
                <Route path='dashboard'>
                    <Route index element={<DashboardPage title="Today"/>}/>
                    <Route path='today' element={<DashboardPage title="Today"/>}></Route>
                    <Route path='future' element={<DashboardPage title="Future"/>}></Route>
                    <Route path='intransit' element={<DashboardPage title="In Transit"/>}></Route>
                    <Route path='map' element={<DashboardPage title="Map"/>}></Route>
                </Route>


                {/* `Not logged in` routes*/}
                <Route element={<ProtectedRoute isAllowed={!isLoggedIn()} redirectPath='/'/>}>
                    <Route path='login' element={<AuthPage/>}/>
                </Route>

                {/* `Logged in` routes*/}
                <Route element={<ProtectedRoute isAllowed={isLoggedIn()} redirectPath='login'/>}>

                    <Route path='login' element={<Navigate to='/' replace/>}/>

                    <Route path='prepare'>
                        <Route index element={<OrdersPage load={load}/>}/>
                        <Route path=':salesorderid' element={<OrdersPage load={load}/>}/>
                    </Route>

                    <Route path='globalsearch' element={<GlobalSearchPage load={load}/>}/>

                    <Route path='fulfilment'>
                        <Route index element={<FulfillmentPage load={load}/>}/>
                        <Route path=':shipmentid' element={<FulfillmentPage load={load}/>}/>
                    </Route>

                    <Route path='scheduler'>
                        <Route index element={<SchedulerPage load={load}/>}/>
                        <Route path=':shipmentid' element={<SchedulerPage load={load}/>}/>
                        <Route path='delete/*' element={<SchedulerPage delete={true} load={load}/>}/>
                    </Route>

                    <Route path='forpickup'>
                        <Route index element={<ForPickupPage load={load}/>}/>
                        <Route path=':shipmentid' element={<ForPickupPage load={load}/>}/>
                        <Route path='delete/*' element={<ForPickupPage load={load} delete={true}/>}/>
                    </Route>

                    <Route path='transit'>
                        <Route index element={<TransitPage load={load}/>}/>
                        <Route path=':shipmentid' element={<TransitPage load={load}/>}/>
                    </Route>

                    <Route path='history'>
                        <Route index element={<HistoryPage load={load}/>}/>
                        <Route path=':shipmentid' element={<HistoryPage load={load}/>}/>
                    </Route>


                    <Route path='production'>
                        <Route index element={<Navigate to={'orders'} replace/>}/>
                        <Route path='reconcile' element={<Reconcile load={load}/>}/>
                        <Route path='shapes'>
                            <Route index element={<ProductionPage load={load} tab={"shapes"}/>}/>
                            <Route path=':shape' element={<ProductionOrderByShape load={load}/>}/>
                        </Route>
                        <Route path='finishes'>
                            <Route index element={<ProductionPage load={load} tab={"finishes"}/>}/>

                            <Route path=':finish' element={<ProductionOrderByFinish load={load}/>}/>
                        </Route>
                        <Route path='orders'>
                            <Route index element={<ProductionPage load={load} tab={"orders"}/>}/>

                            <Route path=':productionorderid' element={<ProductionOrder load={load}/>}/>
                        </Route>
                        <Route path=':quality' element={<QualityPage load={load}/>}/>

                        <Route path='qchold'>
                            <Route index element={<QCIssuedPage load={load}/>}/>
                            <Route path=':issueid' element={<Issue load={load}/>}/>
                        </Route>
                    </Route>

                    <Route path='freeitems'>
                        <Route index element={<FreeItemsPage load={load}/>}/>
                        <Route path=':code' element={<EditFreeItem load={load}/>}/>
                    </Route>

                    <Route path='targets' element={<TargetsPage load={load}/>}/>

                    <Route element={<ProtectedRoute isAllowed={authCtx.hasRole('shipping')}/>}>
                        <Route path='couriers' element={<CouriersPage load={load}/>}/>
                    </Route>

                    <Route element={<ProtectedRoute isAllowed={authCtx.hasRole('fulfilment')}/>}>
                        <Route path='dims' element={<DimsPage load={load}/>}/>
                    </Route>
                    <Route element={<ProtectedRoute isAllowed={authCtx.hasRole('fulfilment')}/>}>
                        <Route path='trackers' element={<TrackersPage load={load}/>}/>
                    </Route>

                    <Route element={<ProtectedRoute isAllowed={authCtx.hasRole('admin')}/>}>

                        <Route path='stations'>
                            <Route index element={<Stations load={load}/>}/>
                            <Route path=':code' element={<Station load={load}/>}/>
                        </Route>

                        <Route path='printers'>
                            <Route index element={<Printers load={load}/>}/>
                        </Route>
                        <Route path='holidays'>
                            <Route index element={<HolidaysPage load={load}/>}/>
                        </Route>
                    </Route>


                    <Route path='audit'>
                        {build === "Development" && <Route path='clone'>
                            <Route index element={<AuditClonedPage load={load}/>}/>
                        </Route>}
                        <Route path='salesorders'>
                            <Route index element={<AuditOrdersPage load={load}/>}/>
                            <Route path=':salesorderid' element={<SalesOrder load={load}/>}/>
                        </Route>

                        <Route path='warehouse' element={<AuditWarehousePage load={load}/>}/>
                        <Route path='shipments'>
                            <Route index element={<AuditShipmentPage load={load}/>}/>
                            <Route path=':shipmentid' element={<Shipment load={load}/>}/>
                        </Route>

                        <Route path='lbship'>
                            <Route path='expected'>
                                <Route index element={<AuditLBShipPage load={load} tab={"expected"}/>}/>
                            </Route>
                            <Route path='allocations'>
                                <Route index element={<AuditLBShipPage load={load} tab={"allocations"}/>}/>
                            </Route>
                            <Route path='orders'>
                                <Route index element={<AuditLBShipPage load={load} tab={"orders"}/>}/>
                            </Route>
                            <Route path='at_risk'>
                                <Route index element={<AuditLBShipPage load={load} tab={"at_risk"}/>}/>
                            </Route>
                            <Route path='ext_uses'>
                                <Route index element={<AuditLBShipPage load={load} tab={"inv_violations"}/>}/>
                            </Route>
                            <Route path='snapshots'>
                                <Route index element={<AuditLBShipPage load={load} tab={"snapshots"}/>}/>
                            </Route>
                        </Route>

                    </Route>

                    <Route path='finance'>
                        <Route index element={<Finance load={load}/>}/>
                    </Route>
                    <Route path='sampleorders'>
                        <Route index element={<Samples load={load}/>}/>
                    </Route>

                    <Route path='logistics'>
                        <Route index element={<Logistics load={load}/>}/>
                    </Route>

                    <Route path='stockitems'>
                        <Route path='stock'>
                            <Route index element={<AuditStockPage load={load} tab={"stock"}/>}/>
                            <Route path=':sku' element={<StockItem load={load}/>}/>
                        </Route>
                        <Route path='lots'>
                            <Route index element={<AuditStockPage load={load} tab={"lots"}/>}/>
                            <Route path=':lotid' element={<AuditStockPage load={load} tab={"lots"}/>}/>
                        </Route>
                    </Route>

                </Route>

                <Route path='*' element={authCtx.isLoggedIn() ? <NotFound/> : <Navigate to={'/'} replace/>}/>

            </Routes>
        </Suspense>
    )
}